



import React, { useState, useMemo, useEffect } from 'react';
import type { Project, Settings, Testimonial, Associate, Video } from '../../types';
import ProjectCard from '../../components/ProjectCard';
import HeroSlider from '../../components/HeroSlider';
import { Link } from 'react-router-dom';
import { usePaginatedCollection } from '../../hooks/usePaginatedCollection';
import { useCollection } from '../../hooks/useCollection';
import OptimizedImage from '../../components/OptimizedImage';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { db } from '../../firebase';
import { writeBatch, doc } from 'firebase/firestore';
import { useToast } from '../../hooks/useToast';
import { useProjectEditor } from './PublicLayout';
import HeroVideo from '../../components/HeroVideo';

import { useSEO } from '../../hooks/useSEO';

interface HomePageProps {
    settings: Settings; 
}

const PlusIcon: React.FC<{className?: string}> = ({className}) => (<svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>);

const ProjectCardSkeleton: React.FC = () => (
    <div className="group relative overflow-hidden cursor-pointer aspect-[4/3] block bg-gray-200 animate-pulse">
        <div className="absolute bottom-0 left-0 p-6 w-full">
            <div className="h-4 bg-gray-300 rounded w-1/3 mb-2"></div>
            <div className="h-8 bg-gray-400 rounded w-2/3"></div>
        </div>
    </div>
);

const FullscreenProject: React.FC<{project: Project}> = ({ project }) => (
    <div className="h-screen w-full relative flex items-center justify-center text-white text-center snap-start flex-shrink-0">
        <OptimizedImage
            src={project.images[0]?.url}
            size={1920}
            fallbackSrc="https://picsum.photos/1920/1080"
            alt={project.title}
            className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative z-10 p-8 animate-fade-in-slow">
            <p className="text-sm uppercase tracking-widest font-header">{project.category}</p>
            <h2 className="text-4xl md:text-6xl font-bold my-4 font-heading">{project.title}</h2>
            <p className="max-w-2xl mx-auto text-lg text-gray-200 font-body">{project.description}</p>
            <Link to={`/project/${project.slug}`} className="mt-8 inline-block px-8 py-3 border border-white text-sm font-semibold uppercase tracking-widest hover:bg-white hover:text-black transition-colors font-header">
                View Project
            </Link>
        </div>
    </div>
);

const SortableProjectCard: React.FC<{ project: Project }> = ({ project }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: project.id });
    const style = { transform: CSS.Transform.toString(transform), transition };

    return (
        <div ref={setNodeRef} style={style}>
            <div {...attributes} {...listeners} className="cursor-grab">
                <ProjectCard project={project} />
            </div>
        </div>
    );
};

const GoogleReviewsSection: React.FC = () => {
    const googleReviews = [
        {
            author_name: "dhillon prabhdial",
            rating: 5,
            relative_time_description: "1 year ago",
            text: "Friendly and Professional Team of Employees and Consultants",
            avatar_color: "bg-indigo-600"
        },
        {
            author_name: "MAZ LIZUARDI SHAHRIZAL BIN ABD RAHMAN",
            rating: 5,
            relative_time_description: "5 years ago",
            text: "Excellent workmen ship",
            avatar_color: "bg-green-600"
        },
        {
            author_name: "Wilson Low",
            rating: 5,
            relative_time_description: "9 years ago",
            text: "Best architect in seremban...",
            avatar_color: "bg-blue-600"
        },
        {
            author_name: "Nor Azura Abu Hassan",
            rating: 5,
            relative_time_description: "6 months ago",
            text: "Recommended professional architecture services.",
            avatar_color: "bg-red-600"
        },
        {
            author_name: "Othman Ahmad",
            rating: 5,
            relative_time_description: "2 years ago",
            text: "Highly recommended for institutional and commercial projects.",
            avatar_color: "bg-yellow-600"
        },
        {
            author_name: "Haziq Mahazir",
            rating: 5,
            relative_time_description: "3 years ago",
            text: "Very helpful and professional throughout the process.",
            avatar_color: "bg-purple-600"
        }
    ];

    const writeReviewUrl = "https://www.google.com/search?q=Arkitek+Norman+Selamat#lrd=0x31cdc773663a0a57:0x43eb40ca161a0f89,3,,,";
    const allReviewsUrl = "https://www.google.com/search?q=Arkitek+Norman+Selamat#lrd=0x31cdc773663a0a57:0x43eb40ca161a0f89,1,,,";

    return (
        <div className="bg-brand-dark text-brand-light py-24 relative overflow-hidden">
            {/* Background design elements */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-brand-light/5 rounded-full blur-3xl pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand-light/5 rounded-full blur-3xl pointer-events-none"></div>

            <div className="container mx-auto px-6 max-w-6xl relative z-10">
                <div className="flex flex-col md:flex-row justify-between items-center mb-16 gap-8">
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"/>
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
                            </svg>
                            <span className="text-sm font-semibold uppercase tracking-wider text-gray-400 font-header">Google Customer Reviews</span>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold font-heading">What Our Clients Say</h2>
                        <p className="text-gray-400 mt-2 font-body max-w-xl">Real reviews from our official Google Business Profile. We take pride in delivering professional architectural excellence.</p>
                    </div>

                    <div className="flex flex-col items-center sm:items-start bg-white/5 backdrop-blur-md p-6 border border-white/10 rounded-xl">
                        <div className="flex items-center gap-4">
                            <span className="text-5xl font-bold font-heading text-white">4.3</span>
                            <div>
                                <div className="flex gap-1 text-[#fbbc05]">
                                    {[...Array(5)].map((_, i) => (
                                        <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                                            {i < 4 ? (
                                                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                                            ) : (
                                                <g>
                                                    <path d="M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4V6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z" />
                                                </g>
                                            )}
                                        </svg>
                                    ))}
                                </div>
                                <p className="text-xs text-gray-400 mt-1 font-body">Based on 21 reviews</p>
                            </div>
                        </div>
                        <div className="flex gap-4 mt-6 w-full">
                            <a 
                                href={writeReviewUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex-1 text-center py-2.5 px-4 bg-brand-light text-brand-dark text-xs font-semibold uppercase tracking-wider rounded hover:bg-gray-200 transition-colors font-header"
                            >
                                Write a Review
                            </a>
                            <a 
                                href={allReviewsUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex-1 text-center py-2.5 px-4 border border-white/20 text-white text-xs font-semibold uppercase tracking-wider rounded hover:bg-white/10 transition-colors font-header"
                            >
                                View All
                            </a>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {googleReviews.map((review, index) => (
                        <div 
                            key={index}
                            className="bg-white/5 backdrop-blur-md p-8 border border-white/10 rounded-xl hover:border-white/20 transition-all duration-300 flex flex-col justify-between group relative"
                        >
                            <div className="absolute top-6 right-6 opacity-20 group-hover:opacity-40 transition-opacity">
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                    <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                    <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"/>
                                    <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
                                </svg>
                            </div>

                            <div>
                                <div className="flex gap-1 text-[#fbbc05] mb-4">
                                    {[...Array(review.rating)].map((_, i) => (
                                        <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                                            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                                        </svg>
                                    ))}
                                </div>
                                <p className="text-gray-200 font-body text-base italic leading-relaxed mb-6">
                                    "{review.text}"
                                </p>
                            </div>

                            <div className="flex items-center gap-4 mt-auto border-t border-white/5 pt-4">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${review.avatar_color}`}>
                                    {review.author_name.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                    <h4 className="font-bold text-sm text-white font-heading">{review.author_name}</h4>
                                    <span className="text-xs text-gray-400 font-body">{review.relative_time_description} • Local Guide</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};


const HomePage: React.FC<HomePageProps> = ({ settings }) => {
    const [activeCategory, setActiveCategory] = useState<string>('All');
    const [yearFilter, setYearFilter] = useState('All');
    const [statusFilter, setStatusFilter] = useState('All');
    const portfolioLayout = settings?.portfolioLayout || 'grid';
    const [isDevMode, setIsDevMode] = useState(false);
    const { showToast } = useToast();
    const [localProjects, setLocalProjects] = useState<Project[]>([]);
    const editorContext = useProjectEditor();

    useSEO({
        title: settings?.seo?.title || (settings?.companyName ? `${settings.companyName} | ${settings.slogan}` : 'ARCH. | Architectural Design'),
        description: settings?.seo?.description,
        ogImage: settings?.openGraph?.imageUrl
    }, [settings]);
    
    useEffect(() => {
        setIsDevMode(sessionStorage.getItem('devMode') === 'true');
    }, []);

    const { 
        data: projects, 
        isLoading, 
        loadMore, 
        hasMore, 
        isLoadingMore 
    } = usePaginatedCollection<Project>(
        'projects', 
        [{ field: 'published', operator: '==', value: true }],
        { field: 'order', direction: 'asc' },
        portfolioLayout === 'fullscreen' ? 100 : 9
    );
    
    const { data: videos } = useCollection<Video>('videos');

    const heroVideo = useMemo(() => {
        return (videos || []).find(v => v.isHero);
    }, [videos]);


    useEffect(() => {
        setLocalProjects(projects);
    }, [projects]);
    
    const { data: allProjects } = useCollection<Project>('projects');
    
    const { categories, years, statuses } = useMemo(() => {
        if (!allProjects) return { categories: ['All'], years: [], statuses: [] };
        const categorySet = new Set<string>();
        const yearSet = new Set<number>();
        const statusSet = new Set<Project['status']>();
        allProjects.filter(p => p.published).forEach(p => {
            categorySet.add(p.category);
            yearSet.add(p.year);
            statusSet.add(p.status);
        });
        return {
            categories: ['All', ...Array.from(categorySet).sort()],
            years: Array.from(yearSet).sort((a, b) => b - a),
            statuses: Array.from(statusSet).sort(),
        };
    }, [allProjects]);

    const filteredProjects = useMemo(() => {
        return localProjects.filter(p => {
            const categoryMatch = activeCategory === 'All' || p.category === activeCategory;
            const yearMatch = yearFilter === 'All' || p.year.toString() === yearFilter;
            const statusMatch = statusFilter === 'All' || p.status === statusFilter;
            return categoryMatch && yearMatch && statusMatch;
        });
    }, [localProjects, activeCategory, yearFilter, statusFilter]);
    
    const featuredProjects = useMemo(() => {
        return (allProjects || [])
            .filter(p => p.published && p.featured)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
    }, [allProjects]);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 10 } }));

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = localProjects.findIndex((p) => p.id === active.id);
            const newIndex = localProjects.findIndex((p) => p.id === over.id);
            const reorderedProjects = arrayMove(localProjects, oldIndex, newIndex);
            
            setLocalProjects(reorderedProjects);

            try {
                const batch = writeBatch(db);
                reorderedProjects.forEach((project, index) => {
                    const projectRef = doc(db, 'projects', project.id);
                    batch.update(projectRef, { order: index });
                });
                await batch.commit();
                showToast('Project order saved.', { type: 'success' });
            } catch (error) {
                showToast('Failed to save project order.', { type: 'error' });
                setLocalProjects(projects); // Revert on error
            }
        }
    };

    const renderPortfolio = () => {
        const skeletonCount = 6;

        if (isLoading && filteredProjects.length === 0) {
             const layoutClass = portfolioLayout === 'masonry' 
                ? "columns-1 md:columns-2 lg:columns-3 gap-1 space-y-1" 
                : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1";
            return (
                <div className={layoutClass}>
                    {Array.from({ length: skeletonCount }).map((_, i) => (
                        <div key={i} className={portfolioLayout === 'masonry' ? 'break-inside-avoid' : ''}>
                             <ProjectCardSkeleton />
                        </div>
                    ))}
                </div>
            )
        }
        
        if (filteredProjects.length === 0) {
            return <p className="text-center col-span-full text-gray-500 py-12">No projects found matching the current filters.</p>;
        }

        if (isDevMode && portfolioLayout !== 'fullscreen' && activeCategory === 'All' && yearFilter === 'All' && statusFilter === 'All') {
            return (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={filteredProjects.map(p => p.id)} strategy={rectSortingStrategy}>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
                            {filteredProjects.map(project => (
                                <SortableProjectCard key={project.id} project={project} />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            );
        }

        switch(portfolioLayout) {
            case 'masonry':
                return (
                    <div className="columns-1 md:columns-2 lg:columns-3 gap-1 space-y-1">
                        {filteredProjects.map(project => (
                            <div key={project.id} className="break-inside-avoid">
                                <ProjectCard project={project} />
                            </div>
                        ))}
                    </div>
                );
            case 'fullscreen':
                 return (
                    <div className="h-screen w-full snap-y snap-mandatory overflow-y-scroll overflow-x-hidden">
                        {filteredProjects.map(project => (
                            <FullscreenProject key={project.id} project={project} />
                        ))}
                    </div>
                );
            case 'grid':
            default:
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
                        {filteredProjects.map(project => (
                            <ProjectCard 
                                key={project.id} 
                                project={project}
                            />
                        ))}
                    </div>
                );
        }
    }


    return (
        <div>
             {isDevMode && (
                <button
                    onClick={() => editorContext?.openProjectEditor()}
                    className="fixed bottom-5 right-5 z-50 px-4 py-3 bg-gray-800 text-white rounded-full shadow-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
                    title="Add New Project"
                >
                    <PlusIcon className="w-5 h-5" />
                    <span className="text-sm font-semibold">New Project</span>
                </button>
            )}
            {portfolioLayout !== 'fullscreen' && (
                heroVideo ? (
                    <HeroVideo video={heroVideo} settings={settings} />
                ) : (
                    <HeroSlider projects={featuredProjects} />
                )
            )}

            <div id="portfolio" className={`${portfolioLayout !== 'fullscreen' ? 'bg-brand-light' : 'bg-brand-dark'}`}>
                {portfolioLayout !== 'fullscreen' && (
                    <div className="container mx-auto py-16 px-4">
                        {isDevMode && activeCategory === 'All' && yearFilter === 'All' && statusFilter === 'All' && (
                             <div className="text-center mb-4 p-2 bg-yellow-100 text-yellow-800 rounded-md text-sm">
                                <b>Developer Mode:</b> Drag and drop projects to reorder.
                            </div>
                        )}
                        <div className="flex justify-center items-center flex-wrap gap-x-6 gap-y-4 mb-12 font-header">
                            <div className="flex justify-center flex-wrap gap-x-6 gap-y-2 md:gap-x-8 border-b pb-4">
                                {categories.map(category => (
                                    <button
                                        key={category}
                                        onClick={() => setActiveCategory(category)}
                                        className={`px-2 py-1 text-sm uppercase tracking-wider font-semibold border-b-2 transition-colors duration-300 ${
                                            activeCategory === category 
                                                ? 'border-brand-dark text-brand-dark' 
                                                : 'border-transparent text-gray-500 hover:text-brand-dark'
                                        }`}
                                    >
                                        {category}
                                    </button>
                                ))}
                            </div>
                             <div className="flex justify-center items-center gap-4 text-sm w-full md:w-auto">
                                {settings.enableYearFilter && (
                                    <select value={yearFilter} onChange={e => setYearFilter(e.target.value)} className="bg-transparent border-b p-2">
                                        <option value="All">All Years</option>
                                        {years.map(year => <option key={year} value={year}>{year}</option>)}
                                    </select>
                                )}
                                {settings.enableStatusFilter && (
                                    <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-transparent border-b p-2">
                                        <option value="All">All Statuses</option>
                                        {statuses.map(status => <option key={status} value={status}>{status}</option>)}
                                    </select>
                                )}
                            </div>
                        </div>
                        {renderPortfolio()}
                        
                        {hasMore && activeCategory === 'All' && (
                            <div className="text-center mt-12">
                                <button
                                    onClick={loadMore}
                                    disabled={isLoadingMore}
                                    className="px-8 py-3 bg-brand-dark text-brand-light font-semibold uppercase tracking-widest hover:bg-gray-800 transition-colors disabled:bg-gray-400 font-header"
                                >
                                    {isLoadingMore ? 'Loading...' : 'Load More'}
                                </button>
                            </div>
                        )}
                    </div>
                )}
                {portfolioLayout === 'fullscreen' && renderPortfolio()}
            </div>

            <GoogleReviewsSection />

            <style>{`
                @keyframes fade-in-slow {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in-slow { animation: fade-in-slow 1s ease-out forwards; }
            `}</style>
        </div>
    );
};

export default HomePage;